import * as React from "react";
import { css } from "@emotion/core";
import { ImagesMachineContext } from "components/Orders/state/imagesMachine";
import { LidarMachineContext } from "components/Orders/state/lidarMachine";
import { GeometryMachineContext } from "components/Orders/state/geometryMachine";
import { OrderMachineContext } from "components/Orders/state/orderMachine";
import ImagesList from "./ImagesList";
import PointCloudList from "./PointCloudList";
import GeometryList from "./GeometryList";

interface UploadListsContainerProps {
  onFocusImage: () => void;
  onFocusPointCloud: (bbox: number[]) => void;
  onFocusGeometry: (bbox: number[]) => void;
  onSetLocationToCenter: () => void;
  uploadInProgress: boolean;
  fileAgeMap: Map<string, boolean>;
}

export default function UploadListsContainer(props: UploadListsContainerProps) {
  const imageUploads = ImagesMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const lidarUploads = LidarMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const geometryUploads = GeometryMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const orderMachine = OrderMachineContext.useActorRef();

  const [longitude, latitude] = OrderMachineContext.useSelector((state) => {
    return [state.context.center?.[0] ?? "0", state.context.center?.[1] ?? "0"];
  });

  const zoom = OrderMachineContext.useSelector((state) => state.context.zoom);

  const imageMapRef = React.useRef<mapboxgl.Map>(null);

  const onZoomEnd = React.useCallback((event: any) => {
    orderMachine.send({
      type: "SET_ZOOM",
      zoom: event.viewState.zoom,
    });
  }, [orderMachine]);

  const mapCenter: [number, number] = [Number(longitude), Number(latitude)];

  const gridTemplateRows = [
    imageUploads.length > 0 ? "1fr" : null,
    lidarUploads.length > 0 ? "minmax(auto, 1fr)" : null,
    geometryUploads.length > 0 ? "minmax(auto, 1fr)" : null,
  ]
    .filter((row) => row !== null)
    .join(" ");

  const hasUploads =
    imageUploads.length > 0 ||
    lidarUploads.length > 0 ||
    geometryUploads.length > 0;

  if (!hasUploads) {
    return null;
  }

  return (
    <div
      css={css`
        flex: 1;
        display: grid;
        grid-template-rows: ${gridTemplateRows};
        overflow: hidden;
      `}
    >
      {imageUploads.length > 0 && (
        <ImagesList
          images={imageUploads}
          onFocus={props.onFocusImage}
          onSetLocationToCenter={props.onSetLocationToCenter}
          uploadInProgress={props.uploadInProgress}
          mapRef={imageMapRef}
          mapCenter={mapCenter}
          zoom={zoom}
          onZoomEnd={onZoomEnd}
          fileAgeMap={props.fileAgeMap}
        />
      )}
      {lidarUploads.length > 0 && (
        <PointCloudList
          pointclouds={lidarUploads}
          onFocus={props.onFocusPointCloud}
          fileAgeMap={props.fileAgeMap}
        />
      )}
      {geometryUploads.length > 0 && (
        <GeometryList
          geometry={geometryUploads}
          onFocus={props.onFocusGeometry}
        />
      )}
    </div>
  );
}
